import type { LanguageModelUsage, ProviderMetadata } from 'ai';
import {
  resolveSupportedResearchReasoningEffort,
  type ResearchReasoningEffort,
} from '@/lib/research-assistant/reasoning-effort';
import type {
  ModelCallUsageMetadata,
  ModelUsageMetadata,
} from '@/lib/research-assistant/runtime/stream-types';

export type ResearchModelProvider =
  | 'openai'
  | 'anthropic'
  | 'meta'
  | 'xai'
  | 'zai'
  | 'deepseek'
  | 'moonshotai';
export type ResearchRuntimeKind = 'ai_sdk_gateway';
export type ResearchModelTier = 'Fast' | 'Regular' | 'Medium' | 'Deep';
export type ResearchModelSpeed = 'Fast' | 'Standard';
export type ResearchModelCost = '$' | '$$' | '$$$';

export interface ResearchModelPricing {
  inputPerTokenUsd: number;
  outputPerTokenUsd: number;
  cachedInputPerTokenUsd?: number;
  cacheWriteInputPerTokenUsd?: number;
  source: 'static' | 'gateway_catalog';
  version: string;
}

export interface ResearchModelConfig {
  id: string;
  apiModelId: string;
  provider: ResearchModelProvider;
  runtimeKind: ResearchRuntimeKind;
  label: string;
  shortLabel: string;
  tier: ResearchModelTier;
  speed: ResearchModelSpeed;
  cost: ResearchModelCost;
  contextWindowTokens: number;
  maxOutputTokens: number;
  description: string;
  supportsImages: boolean;
  supportsFiles: boolean;
  supportedFileMimeTypes?: readonly string[];
  supportsTools: boolean;
  supportsResponsesContinuation: boolean;
  supportsReasoning: boolean;
  reasoningEfforts?: readonly ResearchReasoningEffort[];
  default?: boolean;
  requiresEnv?: string;
  pricing: ResearchModelPricing;
}

export interface PublicResearchModel {
  id: string;
  provider: ResearchModelProvider;
  runtimeKind: ResearchRuntimeKind;
  label: string;
  shortLabel: string;
  tier: ResearchModelTier;
  speed: ResearchModelSpeed;
  cost: ResearchModelCost;
  description: string;
  supportsImages: boolean;
  supportsFiles: boolean;
  supportedFileMimeTypes?: string[];
  supportsTools: boolean;
  supportsReasoning: boolean;
  supportsReasoningEffort: boolean;
  reasoningEfforts: ResearchReasoningEffort[];
  default?: boolean;
  available: boolean;
  disabledReason?: string;
  currentOnly?: boolean;
}

export interface ResearchModelSelection {
  model: ResearchModelConfig;
  requestedModel?: ResearchModelConfig;
  requestedModelId?: string;
  unavailableReason?: string;
}

export interface ResearchUsageBreakdown {
  provider: ResearchModelProvider;
  model: string;
  pricedAsModel: string;
  inputTokens: number;
  processedInputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  reasoningTokens: number;
  toolTokens: number;
  totalTokens: number;
  costUsd: number;
  pricingSource: ResearchModelPricing['source'];
  pricingVersion: string;
}

export interface ResearchUsageReport {
  modelId: string;
  apiModelId: string;
  provider: ResearchModelProvider;
  runtimeKind: ResearchRuntimeKind;
  inputTokens: number;
  processedInputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  reasoningTokens: number;
  toolTokens: number;
  totalTokens: number;
  costUsd: number;
  tokenSource: 'provider' | 'estimated';
  pricingVersion: string;
  breakdown: ResearchUsageBreakdown[];
  callCount: number;
  peakInputTokens: number;
  gatewayGenerationId?: string;
  rawProviderMetadata?: ProviderMetadata;
}

const PRICE_VERSION = 'gateway-catalog-2026-07-30';
export const DEFAULT_RESEARCH_MODEL_ID = 'openai/gpt-6-luna';
export const ONBOARDING_RESEARCH_MODEL_ID = 'openai/gpt-6-sol';
export const RECOMMENDED_RESEARCH_MODEL_ID = 'openai/gpt-6-luna';

const perToken = (perMillion: number): number => perMillion / 1_000_000;
const staticPricing = (
  inputPerMillion: number,
  outputPerMillion: number,
  cachedInputPerMillion?: number
): ResearchModelPricing => ({
  inputPerTokenUsd: perToken(inputPerMillion),
  outputPerTokenUsd: perToken(outputPerMillion),
  ...(cachedInputPerMillion == null
    ? {}
    : { cachedInputPerTokenUsd: perToken(cachedInputPerMillion) }),
  source: 'static',
  version: PRICE_VERSION,
});

// Capability tiers are verified against the live AI Gateway catalog and
// scripts/validation/research-gateway-model-smoke.ts. Stored continuation uses
// Gateway's persistent Responses WebSocket transport; its HTTP endpoint accepts
// previous_response_id without reliably restoring the prior task.
const RESEARCH_MODEL_REGISTRY: ResearchModelConfig[] = [
  {
    id: 'anthropic/claude-opus-5',
    apiModelId: 'anthropic/claude-opus-5',
    provider: 'anthropic',
    runtimeKind: 'ai_sdk_gateway',
    label: 'Claude Opus 5',
    shortLabel: 'Opus 5',
    tier: 'Deep',
    speed: 'Standard',
    cost: '$$$',
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 128_000,
    description:
      'Anthropic flagship for agentic coding, professional work, and long-horizon reasoning.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['low', 'medium', 'high'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000005,
      outputPerTokenUsd: 0.000025,
      cachedInputPerTokenUsd: 0.0000005,
      cacheWriteInputPerTokenUsd: 0.00000625,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-25',
    },
  },
  {
    id: 'moonshotai/kimi-k3',
    apiModelId: 'moonshotai/kimi-k3',
    provider: 'moonshotai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'Kimi K3',
    shortLabel: 'Kimi K3',
    tier: 'Deep',
    speed: 'Standard',
    cost: '$$',
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 131_072,
    description: 'Kimi flagship for long-horizon coding and end-to-end knowledge work.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000003,
      outputPerTokenUsd: 0.000015,
      cachedInputPerTokenUsd: 0.0000003,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'openai/gpt-6.1-sol',
    apiModelId: 'openai/gpt-6.1-sol',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 6.1 Sol',
    shortLabel: '6.1 Sol',
    tier: 'Deep',
    speed: 'Standard',
    cost: '$$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'Near-Astra performance for complex research, coding, and professional work.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    requiresEnv: 'OPENAI_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000002,
      outputPerTokenUsd: 0.00001,
      cachedInputPerTokenUsd: 0.0000001,
      cacheWriteInputPerTokenUsd: 0.0000025,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-29',
    },
  },
  {
    id: 'openai/gpt-6.1-sol-fast',
    apiModelId: 'openai/gpt-6.1-sol-fast',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 6.1 Sol Fast',
    shortLabel: '6.1 Sol Fast',
    tier: 'Deep',
    speed: 'Fast',
    cost: '$$$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'Sol 6.1 with priority processing, at twice the standard token price.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    requiresEnv: 'OPENAI_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000004,
      outputPerTokenUsd: 0.00002,
      cachedInputPerTokenUsd: 0.0000002,
      cacheWriteInputPerTokenUsd: 0.000005,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-29',
    },
  },
  {
    id: 'openai/gpt-6-sol',
    apiModelId: 'openai/gpt-6-sol',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 6 Sol',
    shortLabel: '6 Sol',
    tier: 'Deep',
    speed: 'Standard',
    cost: '$$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'GPT-6 Sol for long-horizon agentic research and coding.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    // Native compaction uses stateless direct OpenAI requests.
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['none', 'low', 'medium', 'high'],
    requiresEnv: 'OPENAI_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000002,
      outputPerTokenUsd: 0.00001,
      cachedInputPerTokenUsd: 0.0000002,
      cacheWriteInputPerTokenUsd: 0.0000025,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-22',
    },
  },
  {
    id: 'openai/gpt-6-luna',
    apiModelId: 'openai/gpt-6-luna',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 6 Luna',
    shortLabel: '6 Luna',
    tier: 'Regular',
    speed: 'Fast',
    cost: '$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'Fast, affordable GPT-6 for responsive agent runs.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    // Native compaction uses stateless direct OpenAI requests.
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['none', 'low', 'medium', 'high'],
    default: true,
    requiresEnv: 'OPENAI_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.0000001,
      outputPerTokenUsd: 0.0000005,
      cachedInputPerTokenUsd: 0.00000001,
      cacheWriteInputPerTokenUsd: 0.000000125,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-22',
    },
  },
  {
    id: 'openai/gpt-6-luna-fast',
    apiModelId: 'openai/gpt-6-luna-fast',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 6 Luna Fast',
    shortLabel: '6 Luna Fast',
    tier: 'Regular',
    speed: 'Fast',
    cost: '$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'Luna with priority processing for lower latency, at twice the standard token price.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    // Native compaction uses stateless direct OpenAI requests.
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
    requiresEnv: 'OPENAI_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.0000002,
      outputPerTokenUsd: 0.000001,
      cachedInputPerTokenUsd: 0.00000002,
      cacheWriteInputPerTokenUsd: 0.00000025,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-26',
    },
  },
  {
    id: 'meta/muse-wonder-1.0',
    apiModelId: 'meta/muse-wonder-1.0',
    provider: 'meta',
    runtimeKind: 'ai_sdk_gateway',
    label: 'Muse Wonder',
    shortLabel: 'Wonder',
    tier: 'Deep',
    speed: 'Standard',
    cost: '$$$',
    contextWindowTokens: 1_048_576,
    maxOutputTokens: 1_048_576,
    description: 'Meta flagship for coding, long-running agents, and computer use.',
    supportsImages: true,
    supportsFiles: true,
    supportedFileMimeTypes: ['application/pdf'],
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['minimal', 'low', 'medium', 'high', 'xhigh'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000004,
      outputPerTokenUsd: 0.00002,
      cachedInputPerTokenUsd: 0.0000002,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-10-12',
    },
  },
  {
    id: 'meta/muse-spark-1.1',
    apiModelId: 'meta/muse-spark-1.1',
    provider: 'meta',
    runtimeKind: 'ai_sdk_gateway',
    label: 'Muse Spark 1.1',
    shortLabel: 'Muse Spark',
    tier: 'Medium',
    speed: 'Standard',
    cost: '$',
    contextWindowTokens: 1_048_576,
    maxOutputTokens: 1_048_576,
    description: 'Meta agent model tuned for tools, computer use, and long-running tasks.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['minimal', 'low', 'medium', 'high', 'xhigh'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.00000125,
      outputPerTokenUsd: 0.00000425,
      cachedInputPerTokenUsd: 0.00000015,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'deepseek/deepseek-v4-flash',
    apiModelId: 'deepseek/deepseek-v4-flash',
    provider: 'deepseek',
    runtimeKind: 'ai_sdk_gateway',
    label: 'DeepSeek V4 Flash',
    shortLabel: 'DeepSeek',
    tier: 'Fast',
    speed: 'Fast',
    cost: '$',
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 384_000,
    description: 'Lowest-cost reasoning lane for high-volume agent runs.',
    supportsImages: false,
    supportsFiles: false,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['high', 'xhigh'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.00000013,
      outputPerTokenUsd: 0.00000026,
      cachedInputPerTokenUsd: 0.000000028,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-25',
    },
  },
  {
    id: 'spacexai/grok-4.7',
    apiModelId: 'spacexai/grok-4.7',
    provider: 'xai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'Grok 4.7',
    shortLabel: 'Grok 4.7',
    tier: 'Medium',
    speed: 'Standard',
    cost: '$$',
    contextWindowTokens: 500_000,
    maxOutputTokens: 500_000,
    description: 'Grok model for long-running research, coding, and professional knowledge work.',
    supportsImages: true,
    supportsFiles: false,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['low', 'medium', 'high'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.0000012,
      outputPerTokenUsd: 0.0000036,
      cachedInputPerTokenUsd: 0.0000003,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-21',
    },
  },
  {
    id: 'zai/glm-5.3-flash',
    apiModelId: 'zai/glm-5.3-flash',
    provider: 'zai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GLM 5.3 Flash',
    shortLabel: 'GLM 5.3 Flash',
    tier: 'Regular',
    speed: 'Fast',
    cost: '$',
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 131_000,
    description: 'Fast, economical GLM lane for tool-using research and coding tasks.',
    supportsImages: true,
    supportsFiles: false,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['high', 'xhigh'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.00000015,
      outputPerTokenUsd: 0.0000005,
      cachedInputPerTokenUsd: 0.00000003,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'zai/glm-5.2',
    apiModelId: 'zai/glm-5.2',
    provider: 'zai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GLM 5.2',
    shortLabel: 'GLM 5.2',
    tier: 'Regular',
    speed: 'Standard',
    cost: '$',
    contextWindowTokens: 1_040_000,
    maxOutputTokens: 128_000,
    description: 'Regular GLM lane; cheaper than GLM 5.2 Fast.',
    supportsImages: false,
    supportsFiles: false,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['high', 'xhigh'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.0000008,
      outputPerTokenUsd: 0.00000255,
      cachedInputPerTokenUsd: 0.00000016,
      source: 'gateway_catalog',
      version: 'gateway-catalog-2026-09-25',
    },
  },
];

// Hidden compatibility entries keep persisted automations stable without
// offering retired models for new selections in the public registry.
const LEGACY_RESEARCH_MODEL_REGISTRY: ResearchModelConfig[] = [
  // Historical pricing only: selection and checkpoint restoration migrate these IDs.
  {
    id: 'openai/gpt-5.6-sol',
    apiModelId: 'openai/gpt-5.6-sol',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 5.6 Sol',
    shortLabel: '5.6 Sol',
    tier: 'Deep',
    speed: 'Standard',
    cost: '$$$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'Flagship GPT-5.6 for long-horizon agentic research and coding.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: true,
    supportsReasoning: true,
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000005,
      outputPerTokenUsd: 0.00003,
      cachedInputPerTokenUsd: 0.0000005,
      cacheWriteInputPerTokenUsd: 0.00000625,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'openai/gpt-5.6-terra',
    apiModelId: 'openai/gpt-5.6-terra',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 5.6 Terra',
    shortLabel: '5.6 Terra',
    tier: 'Medium',
    speed: 'Standard',
    cost: '$$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'Balanced GPT-5.6 for everyday agent work and research.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: true,
    supportsReasoning: true,
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000002,
      outputPerTokenUsd: 0.000012,
      cachedInputPerTokenUsd: 0.0000002,
      cacheWriteInputPerTokenUsd: 0.0000025,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'openai/gpt-5.6-luna',
    apiModelId: 'openai/gpt-5.6-luna',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 5.6 Luna',
    shortLabel: '5.6 Luna',
    tier: 'Regular',
    speed: 'Fast',
    cost: '$',
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    description: 'Fast, affordable GPT-5.6 for responsive agent runs.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: true,
    supportsReasoning: true,
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
    default: true,
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.0000002,
      outputPerTokenUsd: 0.0000012,
      cachedInputPerTokenUsd: 0.00000002,
      cacheWriteInputPerTokenUsd: 0.00000025,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },

  {
    id: 'xai/grok-4.6',
    apiModelId: 'xai/grok-4.6',
    provider: 'xai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'Grok 4.6',
    shortLabel: 'Grok 4.6',
    tier: 'Medium',
    speed: 'Standard',
    cost: '$$',
    contextWindowTokens: 500_000,
    maxOutputTokens: 500_000,
    description: 'xAI model for long-running agents and interactive visual work.',
    supportsImages: true,
    supportsFiles: false,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['low', 'medium', 'high'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000002,
      outputPerTokenUsd: 0.000006,
      cachedInputPerTokenUsd: 0.0000005,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'xai/grok-4.5',
    apiModelId: 'xai/grok-4.5',
    provider: 'xai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'Grok 4.5',
    shortLabel: 'Grok 4.5',
    tier: 'Medium',
    speed: 'Standard',
    cost: '$$',
    contextWindowTokens: 500_000,
    maxOutputTokens: 500_000,
    description: 'xAI Gateway model for agentic coding, research, and cross-provider checks.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['low', 'medium', 'high'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000002,
      outputPerTokenUsd: 0.000006,
      cachedInputPerTokenUsd: 0.0000005,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'openai/gpt-5.5',
    apiModelId: 'openai/gpt-5.5',
    provider: 'openai',
    runtimeKind: 'ai_sdk_gateway',
    label: 'GPT 5.5 (legacy)',
    shortLabel: '5.5',
    tier: 'Medium',
    speed: 'Standard',
    cost: '$$$',
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 128_000,
    description: 'Compatibility model for existing saved automations.',
    supportsImages: true,
    supportsFiles: true,
    supportsTools: true,
    supportsResponsesContinuation: true,
    supportsReasoning: true,
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.000005,
      outputPerTokenUsd: 0.00003,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
  {
    id: 'deepseek/deepseek-v4-pro',
    apiModelId: 'deepseek/deepseek-v4-pro',
    provider: 'deepseek',
    runtimeKind: 'ai_sdk_gateway',
    label: 'DeepSeek V4 Pro (legacy)',
    shortLabel: 'DeepSeek Pro',
    tier: 'Deep',
    speed: 'Standard',
    cost: '$',
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 384_000,
    description: 'Compatibility model for existing saved automations.',
    supportsImages: false,
    supportsFiles: false,
    supportsTools: true,
    supportsResponsesContinuation: false,
    supportsReasoning: true,
    reasoningEfforts: ['high', 'xhigh'],
    requiresEnv: 'AI_GATEWAY_API_KEY',
    pricing: {
      inputPerTokenUsd: 0.00000174,
      outputPerTokenUsd: 0.00000348,
      source: 'gateway_catalog',
      version: PRICE_VERSION,
    },
  },
];

const MODEL_BY_ID = new Map(
  [...RESEARCH_MODEL_REGISTRY, ...LEGACY_RESEARCH_MODEL_REGISTRY].map(model => [model.id, model])
);
// Resolve saved selections and environment overrides to the replacement models.
const RESEARCH_MODEL_REPLACEMENTS: Readonly<Record<string, string>> = {
  'openai/gpt-5.6-sol': 'openai/gpt-6-sol',
  'openai/gpt-5.6-terra': 'openai/gpt-6-sol',
  'openai/gpt-5.6-luna': 'openai/gpt-6-luna',
};

export function resolveResearchModelId(modelId: string): string {
  return RESEARCH_MODEL_REPLACEMENTS[modelId] || modelId;
}

export function migrateResearchModelConfig(model: ResearchModelConfig): ResearchModelConfig {
  const replacement = RESEARCH_MODEL_REPLACEMENTS[model.id];
  return replacement ? MODEL_BY_ID.get(replacement)! : model;
}

const NON_SELECTABLE_RESEARCH_MODEL_IDS: ReadonlySet<string> = new Set([
  'meta/muse-spark-1.1',
  'deepseek/deepseek-v4-flash',
  'zai/glm-5.2',
  'moonshotai/kimi-k3',
]);
const DEFAULT_MODEL =
  MODEL_BY_ID.get(DEFAULT_RESEARCH_MODEL_ID) ||
  RESEARCH_MODEL_REGISTRY.find(model => model.default) ||
  RESEARCH_MODEL_REGISTRY[0];

function hasEnv(name?: string): boolean {
  if (!name) return true;
  return Boolean(process.env[name]?.trim());
}

export function listResearchModels(): ResearchModelConfig[] {
  return RESEARCH_MODEL_REGISTRY;
}

export function getDefaultResearchModel(): ResearchModelConfig {
  const configuredDefault = process.env.RESEARCH_DEFAULT_MODEL_ID?.trim();
  if (configuredDefault) {
    const configuredModel = MODEL_BY_ID.get(resolveResearchModelId(configuredDefault));
    if (configuredModel && isResearchModelAvailable(configuredModel)) {
      return configuredModel;
    }
  }
  if (isResearchModelAvailable(DEFAULT_MODEL)) {
    return DEFAULT_MODEL;
  }
  return RESEARCH_MODEL_REGISTRY.find(isResearchModelAvailable) || DEFAULT_MODEL;
}

export function resolveResearchModel(requestedModelId?: unknown): ResearchModelConfig {
  const selection = resolveResearchModelSelection(requestedModelId);
  if (!selection.unavailableReason) {
    return selection.model;
  }
  return getDefaultResearchModel();
}

function buildResearchModelSelection(
  model: ResearchModelConfig,
  options: Omit<ResearchModelSelection, 'model' | 'unavailableReason'> = {}
): ResearchModelSelection {
  const unavailableReason = getResearchModelUnavailableReason(model);
  return {
    model,
    ...options,
    ...(unavailableReason ? { unavailableReason } : {}),
  };
}

export function resolveResearchModelSelection(requestedModelId?: unknown): ResearchModelSelection {
  if (typeof requestedModelId === 'string' && requestedModelId.trim()) {
    const normalizedModelId = requestedModelId.trim();
    const requested = MODEL_BY_ID.get(resolveResearchModelId(normalizedModelId));
    if (requested) {
      return buildResearchModelSelection(requested, {
        requestedModel: requested,
        requestedModelId: normalizedModelId,
      });
    }
    return {
      model: getDefaultResearchModel(),
      requestedModelId: normalizedModelId,
      unavailableReason: `Unknown research model: ${normalizedModelId}`,
    };
  }

  return buildResearchModelSelection(getDefaultResearchModel());
}

export function isResearchModelAvailable(model: ResearchModelConfig): boolean {
  if (
    model.runtimeKind === 'ai_sdk_gateway' &&
    /^openai\/gpt-6(?:\.\d+)?(?:-|$)/i.test(model.apiModelId)
  ) {
    return hasEnv('OPENAI_API_KEY');
  }
  if (model.runtimeKind === 'ai_sdk_gateway') {
    return hasEnv('AI_GATEWAY_API_KEY') || hasEnv('VERCEL_OIDC_TOKEN');
  }
  return hasEnv(model.requiresEnv);
}

export function supportsResearchModelReasoningEffort(
  model: ResearchModelConfig,
  effort?: ResearchReasoningEffort
): boolean {
  const supportedEfforts = model.reasoningEfforts || [];
  return effort ? supportedEfforts.includes(effort) : supportedEfforts.length > 0;
}

export function resolveResearchModelReasoningEffort(
  model: ResearchModelConfig,
  value: unknown
): ResearchReasoningEffort | undefined {
  return resolveSupportedResearchReasoningEffort(model.reasoningEfforts || [], value);
}

export function getResearchModelUnavailableReason(model: ResearchModelConfig): string | undefined {
  if (isResearchModelAvailable(model)) {
    return undefined;
  }
  return `${model.label} requires ${model.requiresEnv || 'AI_GATEWAY_API_KEY'}`;
}

export function toPublicResearchModel(model: ResearchModelConfig): PublicResearchModel {
  const available = isResearchModelAvailable(model);
  const disabledReason = getResearchModelUnavailableReason(model);
  return {
    id: model.id,
    provider: model.provider,
    runtimeKind: model.runtimeKind,
    label: model.label,
    shortLabel: model.shortLabel,
    tier: model.tier,
    speed: model.speed,
    cost: model.cost,
    description: model.description,
    supportsImages: model.supportsImages,
    supportsFiles: model.supportsFiles,
    ...(model.supportedFileMimeTypes
      ? { supportedFileMimeTypes: [...model.supportedFileMimeTypes] }
      : {}),
    supportsTools: model.supportsTools,
    supportsReasoning: model.supportsReasoning,
    supportsReasoningEffort: supportsResearchModelReasoningEffort(model),
    reasoningEfforts: [...(model.reasoningEfforts || [])],
    default: model.default,
    available,
    ...(available || !disabledReason
      ? {}
      : {
          disabledReason,
        }),
  };
}

export function listPublicResearchModels(): PublicResearchModel[] {
  return RESEARCH_MODEL_REGISTRY.filter(model => !NON_SELECTABLE_RESEARCH_MODEL_IDS.has(model.id))
    .map(toPublicResearchModel)
    .sort((left, right) => {
      if (left.id === RECOMMENDED_RESEARCH_MODEL_ID) return -1;
      if (right.id === RECOMMENDED_RESEARCH_MODEL_ID) return 1;
      if (left.id === 'anthropic/claude-opus-5') return 1;
      if (right.id === 'anthropic/claude-opus-5') return -1;
      return 0;
    });
}

export function getPersistedResearchModelOption(
  modelId?: string | null
): PublicResearchModel | null {
  const normalizedModelId = String(modelId || '').trim();
  if (!normalizedModelId) return null;
  const model = MODEL_BY_ID.get(resolveResearchModelId(normalizedModelId));
  if (!model) return null;
  return {
    ...toPublicResearchModel(model),
    id: normalizedModelId,
    available: false,
    disabledReason: 'Current saved model. Choose another model to replace it.',
    currentOnly: true,
  };
}

function resolvePricing(
  modelId: string,
  fallback: ResearchModelConfig
): {
  pricedAsModel: string;
  pricing: ResearchModelPricing;
  model?: ResearchModelConfig;
} {
  const direct = MODEL_BY_ID.get(modelId);
  if (direct) {
    return { pricedAsModel: direct.apiModelId, pricing: direct.pricing, model: direct };
  }

  const normalized = modelId.replace(/^models\//, '').toLowerCase();
  const matched = [...RESEARCH_MODEL_REGISTRY, ...LEGACY_RESEARCH_MODEL_REGISTRY].find(model => {
    const apiModel = model.apiModelId.toLowerCase();
    return normalized === apiModel || normalized.startsWith(`${apiModel}-`);
  });
  if (matched) {
    return { pricedAsModel: matched.apiModelId, pricing: matched.pricing, model: matched };
  }

  return { pricedAsModel: fallback.apiModelId, pricing: fallback.pricing, model: fallback };
}

function costFor(
  pricing: ResearchModelPricing,
  inputTokens: number,
  outputTokens: number,
  cachedInputTokens = 0,
  cacheWriteTokens = 0
): number {
  const cachedInputRate = pricing.cachedInputPerTokenUsd ?? pricing.inputPerTokenUsd;
  const cacheWriteInputRate = pricing.cacheWriteInputPerTokenUsd ?? pricing.inputPerTokenUsd;
  return (
    inputTokens * pricing.inputPerTokenUsd +
    cachedInputTokens * cachedInputRate +
    cacheWriteTokens * cacheWriteInputRate +
    outputTokens * pricing.outputPerTokenUsd
  );
}

function usageInputTokens(usage: {
  promptTokenCount: number;
  toolUsePromptTokenCount?: number;
  cachedContentTokenCount?: number;
  cacheWriteTokenCount?: number;
}): number {
  return Math.max(
    0,
    usage.promptTokenCount +
      (usage.toolUsePromptTokenCount || 0) -
      (usage.cachedContentTokenCount || 0) -
      (usage.cacheWriteTokenCount || 0)
  );
}

function usageProcessedInputTokens(usage: {
  promptTokenCount: number;
  toolUsePromptTokenCount?: number;
}): number {
  return Math.max(0, usage.promptTokenCount + (usage.toolUsePromptTokenCount || 0));
}

function usageOutputTokens(usage: {
  candidatesTokenCount: number;
  thoughtsTokenCount?: number;
}): number {
  return usage.candidatesTokenCount + (usage.thoughtsTokenCount || 0);
}

function buildAggregatedUsageReport(params: {
  model: ResearchModelConfig;
  usageMetadata: ModelUsageMetadata | null;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
}): ResearchUsageReport {
  const { model, usageMetadata } = params;
  const rawUsages =
    usageMetadata?.modelUsages && usageMetadata.modelUsages.length > 0
      ? usageMetadata.modelUsages
      : [
          {
            model: usageMetadata?.model || model.apiModelId,
            promptTokenCount: usageMetadata?.promptTokenCount ?? params.estimatedInputTokens,
            candidatesTokenCount:
              usageMetadata?.candidatesTokenCount ?? params.estimatedOutputTokens,
            totalTokenCount:
              usageMetadata?.totalTokenCount ??
              params.estimatedInputTokens + params.estimatedOutputTokens,
            thoughtsTokenCount: usageMetadata?.thoughtsTokenCount,
            toolUsePromptTokenCount: usageMetadata?.toolUsePromptTokenCount,
            cachedContentTokenCount: usageMetadata?.cachedContentTokenCount,
            cacheWriteTokenCount: usageMetadata?.cacheWriteTokenCount,
          },
        ];

  const breakdown = rawUsages.map(usage => {
    const inputTokens = usageInputTokens(usage);
    const processedInputTokens = usageProcessedInputTokens(usage);
    const outputTokens = usageOutputTokens(usage);
    const cachedInputTokens = usage.cachedContentTokenCount || 0;
    const cacheWriteTokens = usage.cacheWriteTokenCount || 0;
    const { pricedAsModel, pricing, model: usageModel } = resolvePricing(usage.model, model);
    return {
      provider: usageModel?.provider || model.provider,
      model: usage.model,
      pricedAsModel,
      inputTokens,
      processedInputTokens,
      outputTokens,
      cachedInputTokens,
      cacheWriteTokens,
      reasoningTokens: usage.thoughtsTokenCount || 0,
      toolTokens: usage.toolUsePromptTokenCount || 0,
      totalTokens: usage.totalTokenCount,
      costUsd: costFor(pricing, inputTokens, outputTokens, cachedInputTokens, cacheWriteTokens),
      pricingSource: pricing.source,
      pricingVersion: pricing.version,
    };
  });

  const totals = breakdown.reduce(
    (acc, row) => ({
      inputTokens: acc.inputTokens + row.inputTokens,
      processedInputTokens: acc.processedInputTokens + row.processedInputTokens,
      outputTokens: acc.outputTokens + row.outputTokens,
      cachedInputTokens: acc.cachedInputTokens + row.cachedInputTokens,
      cacheWriteTokens: acc.cacheWriteTokens + row.cacheWriteTokens,
      reasoningTokens: acc.reasoningTokens + row.reasoningTokens,
      toolTokens: acc.toolTokens + row.toolTokens,
      totalTokens: acc.totalTokens + row.totalTokens,
      costUsd: acc.costUsd + row.costUsd,
    }),
    {
      inputTokens: 0,
      processedInputTokens: 0,
      outputTokens: 0,
      cachedInputTokens: 0,
      cacheWriteTokens: 0,
      reasoningTokens: 0,
      toolTokens: 0,
      totalTokens: 0,
      costUsd: 0,
    }
  );

  const finalUsageModel = resolvePricing(
    rawUsages[rawUsages.length - 1]?.model || model.id,
    model
  ).model;
  return {
    modelId: finalUsageModel?.id || model.id,
    apiModelId: finalUsageModel?.apiModelId || model.apiModelId,
    provider: finalUsageModel?.provider || model.provider,
    runtimeKind: finalUsageModel?.runtimeKind || model.runtimeKind,
    tokenSource: usageMetadata ? 'provider' : 'estimated',
    pricingVersion: finalUsageModel?.pricing.version || model.pricing.version,
    breakdown,
    callCount: rawUsages.length,
    peakInputTokens: rawUsages.reduce(
      (peak, usage) => Math.max(peak, usageProcessedInputTokens(usage)),
      0
    ),
    ...totals,
  };
}

export function buildAiSdkUsageReport(params: {
  model: ResearchModelConfig;
  usage: LanguageModelUsage | null;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  providerMetadata?: ProviderMetadata;
  modelUsages?: ModelCallUsageMetadata[];
  finalModelId?: string;
}): ResearchUsageReport {
  const { model, usage } = params;
  if (params.modelUsages && params.modelUsages.length > 0) {
    const promptTokenCount = params.modelUsages.reduce(
      (sum, item) => sum + item.promptTokenCount,
      0
    );
    const candidatesTokenCount = params.modelUsages.reduce(
      (sum, item) => sum + item.candidatesTokenCount,
      0
    );
    const totalTokenCount = params.modelUsages.reduce((sum, item) => sum + item.totalTokenCount, 0);
    const report = buildAggregatedUsageReport({
      model,
      usageMetadata: {
        promptTokenCount,
        candidatesTokenCount,
        totalTokenCount,
        model: params.finalModelId || params.modelUsages[params.modelUsages.length - 1]?.model,
        modelUsages: params.modelUsages,
      },
      estimatedInputTokens: params.estimatedInputTokens,
      estimatedOutputTokens: params.estimatedOutputTokens,
    });
    const gatewayMetadata = params.providerMetadata?.gateway;
    const gatewayGenerationId =
      gatewayMetadata && typeof gatewayMetadata === 'object' && 'generationId' in gatewayMetadata
        ? String((gatewayMetadata as Record<string, unknown>).generationId)
        : undefined;
    const finalModel = resolvePricing(
      params.finalModelId || params.modelUsages[params.modelUsages.length - 1]!.model,
      model
    ).model;
    return {
      ...report,
      modelId: finalModel?.id || report.modelId,
      apiModelId: finalModel?.apiModelId || report.apiModelId,
      provider: finalModel?.provider || report.provider,
      runtimeKind: finalModel?.runtimeKind || report.runtimeKind,
      pricingVersion: finalModel?.pricing.version || report.pricingVersion,
      gatewayGenerationId,
      rawProviderMetadata: params.providerMetadata,
    };
  }
  const cacheReadTokens =
    usage?.inputTokenDetails?.cacheReadTokens || usage?.cachedInputTokens || 0;
  const cacheWriteTokens = usage?.inputTokenDetails?.cacheWriteTokens || 0;
  const processedInputTokens = usage?.inputTokens ?? params.estimatedInputTokens;
  const inputTokens = Math.max(
    0,
    usage?.inputTokenDetails?.noCacheTokens ??
      processedInputTokens - cacheReadTokens - cacheWriteTokens
  );
  const outputTokens = usage?.outputTokens ?? params.estimatedOutputTokens;
  const reasoningTokens = usage?.outputTokenDetails?.reasoningTokens || usage?.reasoningTokens || 0;
  const cachedInputTokens = cacheReadTokens;
  const totalTokens = usage?.totalTokens ?? processedInputTokens + outputTokens;
  const { pricedAsModel, pricing } = resolvePricing(model.apiModelId, model);
  const breakdown: ResearchUsageBreakdown[] = [
    {
      provider: model.provider,
      model: model.apiModelId,
      pricedAsModel,
      inputTokens,
      processedInputTokens,
      outputTokens,
      cachedInputTokens,
      cacheWriteTokens,
      reasoningTokens,
      toolTokens: 0,
      totalTokens,
      costUsd: costFor(pricing, inputTokens, outputTokens, cachedInputTokens, cacheWriteTokens),
      pricingSource: pricing.source,
      pricingVersion: pricing.version,
    },
  ];
  const gatewayMetadata = params.providerMetadata?.gateway;
  const gatewayGenerationId =
    gatewayMetadata && typeof gatewayMetadata === 'object' && 'generationId' in gatewayMetadata
      ? String((gatewayMetadata as Record<string, unknown>).generationId)
      : undefined;

  return {
    modelId: model.id,
    apiModelId: model.apiModelId,
    provider: model.provider,
    runtimeKind: model.runtimeKind,
    inputTokens,
    processedInputTokens,
    outputTokens,
    cachedInputTokens,
    cacheWriteTokens,
    reasoningTokens,
    toolTokens: 0,
    totalTokens,
    costUsd: breakdown[0].costUsd,
    tokenSource: usage ? 'provider' : 'estimated',
    pricingVersion: pricing.version,
    breakdown,
    callCount: usage ? 1 : 0,
    peakInputTokens: processedInputTokens,
    gatewayGenerationId,
    rawProviderMetadata: params.providerMetadata,
  };
}
